import { useState, useRef } from 'react';
import { Camera, Youtube, X, Plus, AlertCircle } from 'lucide-react';

interface PhotoFile {
  file: File;
  preview: string;
  caption: string;
}

interface VideoLink {
  url: string;
  caption: string;
}

interface Props {
  photos: PhotoFile[];
  setPhotos: (photos: PhotoFile[]) => void;
  videoLinks: VideoLink[];
  setVideoLinks: (links: VideoLink[]) => void;
}

const MAX_PHOTOS = 5;
const MAX_FILE_SIZE_KB = 400;

/**
 * Compress an image file to fit within maxSizeKB
 */
async function compressImage(file: File, maxSizeKB: number, maxWidth = 1200): Promise<File> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, width, height);

      // Try decreasing quality until within size limit
      let quality = 0.8;
      const tryCompress = () => {
        canvas.toBlob(
          (blob) => {
            if (blob && (blob.size <= maxSizeKB * 1024 || quality <= 0.3)) {
              resolve(new File([blob], file.name, { type: 'image/jpeg' }));
            } else {
              quality -= 0.1;
              tryCompress();
            }
          },
          'image/jpeg',
          quality
        );
      };
      tryCompress();
    };
    img.src = URL.createObjectURL(file);
  });
}

export function MediaUploadSection({ photos, setPhotos, videoLinks, setVideoLinks }: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [compressing, setCompressing] = useState(false);
  const [error, setError] = useState('');

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) {
      setError(`Máximo ${MAX_PHOTOS} fotos permitidas`);
      return;
    }

    setCompressing(true);
    setError('');

    try {
      const newPhotos: PhotoFile[] = [];
      for (const file of files.slice(0, remaining)) {
        // Validate type
        if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
          continue;
        }

        // Compress if needed
        let processedFile = file;
        if (file.size > MAX_FILE_SIZE_KB * 1024) {
          processedFile = await compressImage(file, MAX_FILE_SIZE_KB);
        }

        const preview = URL.createObjectURL(processedFile);
        newPhotos.push({ file: processedFile, preview, caption: '' });
      }

      setPhotos([...photos, ...newPhotos]);
    } catch {
      setError('Error al procesar las imágenes');
    } finally {
      setCompressing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index: number) => {
    URL.revokeObjectURL(photos[index].preview);
    setPhotos(photos.filter((_, i) => i !== index));
  };

  const addVideoLink = () => {
    setVideoLinks([...videoLinks, { url: '', caption: '' }]);
  };

  const updateVideoLink = (index: number, field: 'url' | 'caption', value: string) => {
    const updated = [...videoLinks];
    updated[index] = { ...updated[index], [field]: value };
    setVideoLinks(updated);
  };

  const removeVideoLink = (index: number) => {
    setVideoLinks(videoLinks.filter((_, i) => i !== index));
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-lg font-medium text-gray-900 mb-2">Fotos y Videos</h2>
      <p className="text-sm text-gray-600 mb-6">
        Sube fotos de a quién ayudará tu campaña para generar confianza. También puedes agregar enlaces de video.
      </p>

      {error && (
        <div className="mb-4 flex items-center gap-2 text-sm text-red-600 bg-red-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Photos section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Camera className="w-4 h-4 inline mr-1" />
          Fotos ({photos.length}/{MAX_PHOTOS})
        </label>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
          {photos.map((photo, index) => (
            <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-gray-200">
              <img src={photo.preview} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto(index)}
                className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Eliminar foto"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}

          {photos.length < MAX_PHOTOS && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={compressing}
              className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-indigo-400 flex flex-col items-center justify-center text-gray-400 hover:text-indigo-500 transition-colors"
            >
              {compressing ? (
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
              ) : (
                <>
                  <Plus className="w-6 h-6 mb-1" />
                  <span className="text-xs">Agregar</span>
                </>
              )}
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <p className="text-xs text-gray-500 mt-2">
          JPG, PNG o WebP. Máximo 400KB por foto (se comprimen automáticamente).
        </p>
      </div>

      {/* Videos section */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          <Youtube className="w-4 h-4 inline mr-1" />
          Enlaces de Video (opcional)
        </label>

        <div className="space-y-3">
          {videoLinks.map((video, index) => (
            <div key={index} className="flex gap-2 items-start">
              <div className="flex-1 space-y-2">
                <input
                  type="url"
                  value={video.url}
                  onChange={(e) => updateVideoLink(index, 'url', e.target.value)}
                  placeholder="https://youtube.com/watch?v=... o enlace de video"
                  className="block w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <input
                  type="text"
                  value={video.caption}
                  onChange={(e) => updateVideoLink(index, 'caption', e.target.value)}
                  placeholder="Descripción del video (opcional)"
                  className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <button
                type="button"
                onClick={() => removeVideoLink(index)}
                className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg"
                aria-label="Eliminar video"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addVideoLink}
            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Agregar enlace de video
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-2">
          YouTube, Vimeo, TikTok, Facebook o Instagram. Se mostrarán como un carousel bonito en la página de tu campaña.
        </p>
      </div>
    </div>
  );
}
